import { useEffect } from 'react';
import { useReactFlow } from 'reactflow';

interface UseKeyboardShortcutsProps {
  onCreateNode: (type: string, position: { x: number; y: number }) => void;
  onDeleteNode?: () => void;
  onDeleteEdge?: () => boolean;
  onEscape?: () => void;
}

const useKeyboardShortcuts = ({
  onCreateNode,
  onDeleteNode,
  onDeleteEdge,
  onEscape
}: UseKeyboardShortcutsProps) => {
  const { project } = useReactFlow();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const { activeElement } = document;
      const isTyping =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement)?.dataset?.slot === 'input' ||
        (activeElement?.tagName === 'DIV' &&
          activeElement?.id === 'japeto-root') ||
        (activeElement as HTMLElement)?.contentEditable === 'true';

      const isOverUIPanel = activeElement?.closest(
        '[data-slot="popover-content"], [data-slot="select-content"], [data-slot="alert-dialog-content"], .MuiPaper-root, .MuiDialog-root, .MuiPopover-root'
      );

      const isModifierKeyPressed = event.metaKey || event.ctrlKey;

      if (event.key === 'Escape' && onEscape) {
        onEscape();
        return;
      }

      if (
        (event.key.toLowerCase() === 'd' ||
          event.key === 'Delete' ||
          event.key === 'Backspace') &&
        (onDeleteNode || onDeleteEdge) &&
        !isTyping &&
        !isOverUIPanel &&
        !isModifierKeyPressed
      ) {
        event.preventDefault();
        event.stopPropagation();

        if (onDeleteEdge) {
          const hasSelectedEdges = onDeleteEdge();
          if (!hasSelectedEdges && onDeleteNode) {
            onDeleteNode();
          }
        } else if (onDeleteNode) {
          onDeleteNode();
        }
        return;
      }

      if (isTyping || isOverUIPanel || isModifierKeyPressed) {
        return;
      }

      const viewport = document.querySelector('.react-flow__viewport');
      if (!viewport) return;

      const viewportRect = viewport.getBoundingClientRect();
      const centerX = viewportRect.width / 2;
      const centerY = viewportRect.height / 2;

      const flowPosition = project({ x: centerX, y: centerY });

      let nodeType = '';

      switch (event.key.toLowerCase()) {
        case 'r':
          nodeType = 'respondToUser';
          break;
        case 'c':
          nodeType = 'collectVariable';
          break;
        case 'f':
          nodeType = 'followUp';
          break;
        case 'g':
          nodeType = 'goToQuestion';
          break;
        case 'v':
          nodeType = 'variableComparison';
          break;
        default:
          return;
      }

      event.preventDefault();
      event.stopPropagation();

      onCreateNode(nodeType, flowPosition);
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCreateNode, onDeleteNode, onDeleteEdge, onEscape, project]);
};

export default useKeyboardShortcuts;
